import { useEffect, useRef } from "react";
import { useAnnotationsPanel } from "~/components/build/Show/lib/useAnnotationsPanel";

// Match the @xl-bbui container breakpoint used in tailwind.config.ts
// (Content 480px + Spacing 40px + Drawer 480px).
const MIN_WIDTH = 1000;

/**
 * Auto-closes the docked annotations panel when the container the panel lives
 * inside crosses below the `@xl-bbui` breakpoint while the panel is open.
 *
 * Above the breakpoint the panel docks alongside the outlet. Below the
 * breakpoint it would render as a full-coverage overlay; rather than letting
 * that happen mid-resize, we close the panel so the user keeps focus on the
 * page content. They can re-open the panel to enter the overlay mode
 * explicitly.
 *
 * Only the wide → narrow transition triggers a close. Opening the panel
 * while already narrow, or widening the viewport with the panel open, are
 * left untouched.
 *
 * The element is resolved lazily via `document.querySelector(selector)` so
 * it can be a sibling rendered later in the tree.
 */
export function useAutoCloseAnnotationsPanelOnNarrow(selector: string) {
  const { isOpen, setOpen } = useAnnotationsPanel();

  // Keep refs in sync so the ResizeObserver callback always sees the latest
  // state without needing to re-create the observer when they change.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  useEffect(() => {
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) {
      return;
    }

    let wasWide = container.clientWidth >= MIN_WIDTH;

    const observer = new ResizeObserver(() => {
      const isWide = container.clientWidth >= MIN_WIDTH;
      if (wasWide && !isWide && isOpenRef.current) {
        setOpenRef.current(false);
      }
      wasWide = isWide;
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [selector]);
}
